// src/components/sales/sections/CallsStatusSection.jsx
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Lightbulb, PhoneCall, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';
import { colorTheme, componentThemes } from '../../../theme/colors';

const CallsStatusSection = ({ data, loading, error, isEmpty }) => {
  if (loading) {
    return (
      <div className={`${componentThemes.card.background} ${componentThemes.card.rounded} ${componentThemes.card.shadow} border ${componentThemes.card.border} ${componentThemes.card.padding}`}>
        <h3 className={`text-lg font-semibold ${componentThemes.text.heading} mb-4 flex items-center`}>
          <PhoneCall className="h-5 w-5 mr-2" />
          Calls by Status
        </h3>
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Loading call status...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${componentThemes.card.background} ${componentThemes.card.rounded} ${componentThemes.card.shadow} border ${componentThemes.card.border} ${componentThemes.card.padding}`}>
        <h3 className={`text-lg font-semibold ${componentThemes.text.heading} mb-4 flex items-center`}>
          <PhoneCall className="h-5 w-5 mr-2" />
          Calls by Status
        </h3>
        <div className="flex items-center justify-center h-48">
          <div className="text-center text-red-600">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusCounts = () => {
    if (!data) return { showed: 0, no_show: 0, canceled: 0, confirmed: 0, total: 0 };

    if (data.showed !== undefined || data.no_show !== undefined) {
      return {
        showed:    data.showed    || 0,
        no_show:   data.no_show   || 0,
        canceled:  data.canceled  || 0,
        confirmed: data.confirmed || 0,
        total: data.total || (data.showed || 0) + (data.no_show || 0) + (data.canceled || 0) + (data.confirmed || 0),
      };
    }

    if (Array.isArray(data)) {
      const counts = { showed: 0, no_show: 0, canceled: 0, confirmed: 0 };
      data.forEach((call) => {
        const status = call.status?.toLowerCase();
        if (counts.hasOwnProperty(status)) counts[status]++;
      });
      return { ...counts, total: Object.values(counts).reduce((s, n) => s + n, 0) };
    }

    if (data.by_status) {
      const s = {
        showed:    data.by_status.showed    || 0,
        no_show:   data.by_status.no_show   || 0,
        canceled:  data.by_status.canceled  || 0,
        confirmed: data.by_status.confirmed || 0,
      };
      return { ...s, total: Object.values(s).reduce((a, n) => a + n, 0) };
    }

    return { showed: 0, no_show: 0, canceled: 0, confirmed: 0, total: 0 };
  };

  const statusCounts = getStatusCounts();
  const total = statusCounts.total;

  // ── Colour map using valid colorTheme keys ────────────────────────────────
  // red   → pink  | orange → brand | blue → brightblue | gray → grey
  const statusConfig = [
    {
      key:       'showed',
      name:      'Showed',
      color:     colorTheme.green.main,
      bgColor:   colorTheme.green.bgLight,
      textColor: colorTheme.green.textKpi,
      bg:        colorTheme.green.bg,
      border:    colorTheme.green.borderLight,
    },
    {
      key:       'no_show',
      name:      'No Show',
      color:     colorTheme.pink.main,         // was red
      bgColor:   colorTheme.pink.bgLight,
      textColor: colorTheme.pink.textKpi,
      bg:        colorTheme.pink.bg,
      border:    colorTheme.pink.borderLight,
    },
    {
      key:       'canceled',
      name:      'Cancelled',
      color:     colorTheme.brand.main,        // was orange
      bgColor:   colorTheme.brand.bgLight,
      textColor: colorTheme.brand.textKpi,
      bg:        colorTheme.brand.bg,
      border:    colorTheme.brand.borderLight,
    },
    {
      key:       'confirmed',
      name:      'Confirmed',
      color:     colorTheme.brightblue.main,   // was blue
      bgColor:   colorTheme.brightblue.bgLight,
      textColor: colorTheme.brightblue.textKpi,
      bg:        colorTheme.brightblue.bg,
      border:    colorTheme.brightblue.borderLight,
    },
  ];

  const pieData = statusConfig
    .map((s) => ({ ...s, value: statusCounts[s.key] }))
    .filter((item) => item.value > 0);

  if (total === 0 || isEmpty) {
    return (
      <div className={`${componentThemes.card.background} ${componentThemes.card.rounded} ${componentThemes.card.shadow} border ${componentThemes.card.border} ${componentThemes.card.padding}`}>
        <h3 className={`text-lg font-semibold ${componentThemes.text.heading} mb-4 flex items-center`}>
          <PhoneCall className="h-5 w-5 mr-2" />
          Calls by Status
        </h3>
        <div className={`text-center ${componentThemes.text.muted} py-8`}>
          <PhoneCall className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No call data available for the selected filters</p>
          <p className="text-sm mt-1">Try adjusting your reference date, period range, or team member selection</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0';
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{d.name}</p>
          <p className="text-brightblue-600">
            <span className="font-medium">{d.value}</span> calls ({pct}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const showedCfg  = statusConfig.find((s) => s.key === 'showed');
  const noShowCfg  = statusConfig.find((s) => s.key === 'no_show');

  return (
    <div className={`${componentThemes.card.background} ${componentThemes.card.rounded} ${componentThemes.card.shadow} border ${componentThemes.card.border} ${componentThemes.card.padding}`}>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-semibold ${componentThemes.text.heading} flex items-center`}>
          <PhoneCall className="h-5 w-5 mr-2" />
          Calls by Status
          {total > 0 && (
            <div className="relative group ml-2">
              <Lightbulb className="h-4 w-4 text-yellow-500 cursor-pointer" />
              <div className="absolute left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-sm text-gray-600 z-10 hidden group-hover:block">
                {(() => {
                  const showRate   = ((statusCounts.showed / total) * 100).toFixed(1);
                  const noShowRate = ((statusCounts.no_show / total) * 100).toFixed(1);
                  return (
                    <>
                      {showRate >= 80 && <div>✅ Excellent show rate ({showRate}%)</div>}
                      {showRate >= 60 && showRate < 80 && <div>👍 Good show rate ({showRate}%)</div>}
                      {showRate < 60  && <div>⚠️ Show rate could be improved ({showRate}%)</div>}
                      {noShowRate > 30 && <div>🔍 High no-show rate ({noShowRate}%) — consider follow-up strategies</div>}
                      {statusCounts.confirmed > 0 && <div>📅 {statusCounts.confirmed} calls confirmed for future</div>}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </h3>
        {data?.closer && data.closer !== 'all' && (
          <span className="bg-gray-100 px-2 py-1 rounded text-xs text-gray-500">{data.closer}</span>
        )}
      </div>

      {/* Pie chart */}
      <div className="h-48 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} dataKey="value">
              {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="space-y-2 mb-4">
        {pieData.map((item, i) => {
          const pct = ((item.value / total) * 100).toFixed(1);
          return (
            <div key={i} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                <span className={componentThemes.text.body}>{item.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-semibold ${item.textColor}`}>{item.value}</span>
                <span className={componentThemes.text.muted}>({pct}%)</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div className={`pt-3 border-t ${colorTheme.grey.borderLight} text-center mb-4`}>
        <span className={`text-sm font-medium ${componentThemes.text.subheading}`}>
          Total Calls: {total}
        </span>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className={`${showedCfg.bg} p-3 rounded-lg border ${showedCfg.border}`}>
          <div className={`text-xs ${componentThemes.text.muted} uppercase tracking-wide`}>Show Rate</div>
          <div className={`text-lg font-bold ${showedCfg.textColor}`}>
            {total > 0 ? ((statusCounts.showed / total) * 100).toFixed(1) : 0}%
          </div>
          <div className="text-xs text-gray-500 mt-1">{statusCounts.showed} of {total}</div>
        </div>
        <div className={`${noShowCfg.bg} p-3 rounded-lg border ${noShowCfg.border}`}>
          <div className={`text-xs ${componentThemes.text.muted} uppercase tracking-wide`}>No-Show Rate</div>
          <div className={`text-lg font-bold ${noShowCfg.textColor}`}>
            {total > 0 ? ((statusCounts.no_show / total) * 100).toFixed(1) : 0}%
          </div>
          <div className="text-xs text-gray-500 mt-1">{statusCounts.no_show} of {total}</div>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="space-y-2">
        <h4 className="font-medium text-gray-700 text-sm mb-2 flex items-center">
          <TrendingUp className="h-4 w-4 mr-1" />
          Status Breakdown:
        </h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {statusConfig.map((s) => {
            const count = statusCounts[s.key];
            const pct   = total > 0 ? ((count / total) * 100).toFixed(1) : '0';
            return (
              <div key={s.key} className="flex justify-between items-center py-1">
                <span className="text-gray-600">{s.name}:</span>
                <span className="font-medium">{count} ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CallsStatusSection;